import { useTranslation } from "react-i18next";
import { useUserStore } from "../../store/userStore";
import type { Company } from "../../store/admin/companiesStore";
import { LogoImage } from "../../components/base/LogoImage";
import CompanySelector from "../../components/base/CompanySelector";
import { useUserCompanyName } from "../../hooks/useUserCompanyName";

interface HeaderProps {
  onOpenAppsSidebar?: () => void;
  onToggleMobileMenu?: () => void;
  isMobileMenuOpen?: boolean;
  isDarkMode?: boolean;
  isReseller?: boolean;
  companies?: Company[];
  selectedCompany?: string | null;
  onCompanyChange?: (company: string | null) => void;
}

export default function Header({
  onOpenAppsSidebar,
  onToggleMobileMenu,
  isMobileMenuOpen,
  isDarkMode = false,
  isReseller = false,
  companies = [],
  selectedCompany,
  onCompanyChange,
}: HeaderProps) {
  const { t } = useTranslation("common");
  const user = useUserStore((state) => state.user);
  const userRole = localStorage.getItem("userRole");
  const userCompanyName = useUserCompanyName();

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-bg" : "bg-[#f5f6f2]",
    border: isDarkMode ? "border-dark-border" : "border-[#e3e6de]",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-500",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-500",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  return (
    <header
      className={`${themeClasses.bg} border-b ${themeClasses.border} px-4 py-2.5 shadow-sm`}
    >
      <div className="flex items-center justify-between">
        {/* Logo and Title */}
        <div className="flex items-center space-x-2 sm:space-x-3">
          {/* Mobile Menu Toggle */}
          {onToggleMobileMenu && (
            <button
              onClick={onToggleMobileMenu}
              className={`lg:hidden p-2 ${themeClasses.buttonHover} ${themeClasses.buttonText} rounded-lg transition-all`}
              aria-label="Toggle menu"
            >
              <i
                className={`${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}
              ></i>
            </button>
          )}
          <LogoImage size="md" />
          <div className="hidden sm:block">
            <h1 className={`text-lg font-bold ${themeClasses.text}`}>
              {t("apps.inspecciones.name")}
            </h1>
            <p className={`text-xs ${themeClasses.textSecondary}`}>
              {t("apps.inspecciones.description")}
            </p>
          </div>

          {/* Company Selector (solo para reseller) */}
          {isReseller && (
            <CompanySelector
              value={selectedCompany ?? null}
              onChange={(id) => onCompanyChange?.(id)}
              isDarkMode={isDarkMode}
              isClearable
              companies={companies}
              className="w-48 sm:w-64 hidden md:block"
            />
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-2">
          {/* Profile - Opens Apps Sidebar */}
          <button
            onClick={onOpenAppsSidebar}
            className={`flex items-center space-x-2 p-1.5 ${themeClasses.buttonHover} rounded-lg transition-all`}
          >
            <div className="w-7 h-7 bg-gradient-to-br from-primary-500 to-primary-400 rounded-lg flex items-center justify-center shadow-sm">
              <span className="text-gray-900 text-xs font-semibold">
                {user?.first_name && user?.last_name
                  ? `${user.first_name.charAt(0).toUpperCase()}${user.last_name.charAt(0).toUpperCase()}`
                  : "DU"}
              </span>
            </div>
            <div className="text-left hidden sm:block">
              <p className={`text-sm font-medium ${themeClasses.text}`}>
                {user?.first_name && user?.last_name
                  ? `${user.first_name} ${user.last_name}`
                  : user?.email || t("general.user")}
              </p>
              <p
                className={`text-xs ${themeClasses.textSecondary} truncate max-w-[200px]`}
              >
                {userCompanyName
                  ? `${userCompanyName} · ${userRole || t("general.user")}`
                  : userRole || t("general.user")}
              </p>
            </div>
            <i
              className={`ri-arrow-down-s-line ${themeClasses.buttonText} text-sm`}
            ></i>
          </button>
        </div>
      </div>
    </header>
  );
}
